import {AsyncInlineError, cache, inlineError} from '@s-ui/decorators'

import NewsRepository from './NewsRepository.js'

/**
 * @class
 * @implements NewsRepository
 */
export default class HTTPNewsRepository extends NewsRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {ArticleImageEntityFactory} deps.articleImageEntityFactory
   * @param {ArticlePreviewEntityFactory} deps.articleEntityFactory
   * @param {Config} deps.config
   * @param {Fetcher} deps.fetcher
   * @param {Fetcher} deps.fetchOrFail
   * @param {Error} deps.getModelDetailNewsError
   * @param {Error} deps.getVehicleNewsError
   * @param {Error} deps.getSubscribeToNewsletterError
   * @param {Function} deps.log
   * @param {HomeNewsValueObjectFactory} deps.homeNewsValueObjectFactory
   * @param {ModelDetailNewsValueObjectFactory} deps.modelDetailNewsValueObjectFactory
   * @param {NewsletterSubscriptionResponseValueObject} deps.newsletterSubscriptionResponseValueObjectFactory
   * @param {NewsSuggestionsValueObject} deps.newsSuggestionsValueObjectFactory
   * @param {TagEntityFactory} deps.tagEntityFactory
   * @param {LatestPlatesValueObjectFactory} deps.latestPlatesValueObjectFactory
   */
  constructor({
    articleEntityFactory,
    articleImageEntityFactory,
    config,
    fetcher,
    fetchOrFail,
    getModelDetailNewsError,
    getVehicleNewsError,
    homeNewsValueObjectFactory,
    log,
    modelDetailNewsValueObjectFactory,
    newsSuggestionsValueObjectFactory,
    newsValueObjectFactory,
    tagEntityFactory,
    toQueryString,
    getNewsDetailError,
    fromNewsDetailResponseToEntityMapper,
    getSubscribeToNewsletterError,
    latestPlatesValueObjectFactory
  }) {
    super()
    this._articleEntityFactory = articleEntityFactory
    this._articleImageEntityFactory = articleImageEntityFactory
    this._categoriesPaths = config.get('editorial').categoriesUrls
    this._homeConfig = config.get('home')
    this._config = config
    this._fetcher = fetcher
    this._fetchOrFail = fetchOrFail
    this._getModelDetailNewsError = getModelDetailNewsError
    this._getVehicleNewsError = getVehicleNewsError
    this._homeNewsValueObjectFactory = homeNewsValueObjectFactory
    this._log = log
    this._modelDetailNewsValueObjectFactory = modelDetailNewsValueObjectFactory
    this._newsSuggestionsValueObjectFactory = newsSuggestionsValueObjectFactory
    this._newsValueObjectFactory = newsValueObjectFactory
    this._relTag = config.get('seo').SPONSORED_REL_TAG
    this._tagEntityFactory = tagEntityFactory
    this._toQueryString = toQueryString
    this._urlCategoryPath = config.get('page').NEWS_CATEGORY_PATH_PATTERN
    this._urlTagPath = config.get('page').NEWS_TAG_PATH_PATTERN
    this._getNewsDetailError = getNewsDetailError
    this._fromNewsDetailResponseToEntityMapper = fromNewsDetailResponseToEntityMapper
    this._getSubscribeToNewsletterError = getSubscribeToNewsletterError
    this._latestPlatesValueObjectFactory = latestPlatesValueObjectFactory
  }

  _generateTagLink = ({path}) => {
    return this._categoriesPaths.includes(path)
      ? this._urlCategoryPath.replace(':category', path)
      : this._urlTagPath.replace(':tag', path)
  }

  _mapRawArticleToEntity = urlBuilder => article =>
    this._articleEntityFactory({
      articleId: article.articleId,
      config: this._config,
      id: article.id,
      img: this._articleImageEntityFactory({
        url: article.imageUrl,
        cutCoordinates: article.cutCoordinates
      }),
      tag:
        article.tag &&
        this._tagEntityFactory({
          id: article.tag.path,
          url: urlBuilder(article.tag),
          text: article.tag.text,
          rel: this._relTag
        }),
      numComments: article.numComments,
      commentsDisabled: article.commentsDisabled,
      description: article.description,
      url: article.url,
      title: article.title,
      isVideo: article.isVideo,
      shouldUseCommentsPage:
        !this._config.get('isMobile') ||
        (this._config.get('isMobile') && this._config.get('editorial').HAS_MOBILE_COMMENTS_PAGE_ENABLED)
    })

  _mapHomeRawArticleToEntity = this._mapRawArticleToEntity(this._generateTagLink)

  /**
   * @return {Array}
   */
  @cache({ttl: '10 minutes', server: true})
  _getHomeNews() {
    this._log('Getting home news items')
    const API_DOMAIN_URL = this._config.get('CONTENTFUL_API_URL')

    const {HOME_SECTION_ID_TESTS, HOME_SECTION_ID_NEWNESS, HOME_SECTION_ID_RANKINGS, HOME_SECTION_ID_GREEN} =
      this._config.get('COMMON')

    return this._fetcher
      .get(`${API_DOMAIN_URL}/news/home`)
      .then(({data: {highlightedGroup1, highlightedGroup2, highlightedGroup3, highlightedGroup4}}) => {
        const {ENABLE_GREEN_MOBILITY_SECTION} = this._homeConfig
        const newsItems = [
          {
            items: highlightedGroup1.map(this._mapHomeRawArticleToEntity),
            sectionId: HOME_SECTION_ID_TESTS
          },
          {
            items: highlightedGroup2.map(this._mapHomeRawArticleToEntity),
            sectionId: HOME_SECTION_ID_NEWNESS,
            sectionUrl: this._generateTagLink({path: HOME_SECTION_ID_NEWNESS})
          },
          {
            items: highlightedGroup3.map(this._mapHomeRawArticleToEntity),
            sectionId: HOME_SECTION_ID_RANKINGS,
            sectionUrl: this._generateTagLink({path: HOME_SECTION_ID_RANKINGS})
          },
          ...(ENABLE_GREEN_MOBILITY_SECTION
            ? [
                {
                  items: highlightedGroup4?.map(this._mapHomeRawArticleToEntity),
                  sectionId: HOME_SECTION_ID_GREEN
                }
              ]
            : [])
        ].filter(Boolean)

        return this._homeNewsValueObjectFactory(newsItems)
      })
  }

  /**
   * @return {Object}
   */
  @cache({ttl: '10 minutes', server: true})
  _getModelDetailNews(params) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {count, offset, year, makeId, modelId} = params
    this._log('Getting brand info news items')

    const queryString = this._toQueryString({
      count,
      makeId,
      modelId,
      offset,
      year
    })

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._getModelDetailNewsError({message})
        }
      })
      .get(`${this._config.get('API_URL')}/vehicle/modeldetail/news?${queryString}`)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)

        const {data} = response
        return this._modelDetailNewsValueObjectFactory(data)
      })
  }

  getHomeNews = () =>
    this._getHomeNews({
      tenant: this._config.get('TENANT')
    })

  getModelDetailNews = params => {
    return this._getModelDetailNews(params)
  }

  _getParsedTerm = term => term?.replace(/-/g, '%20')

  _getSearchTerm = ({category, tag, searchTerm, section}) => {
    const parsedTag = this._getParsedTerm(tag)
    const parsedSearchedTerm = this._getParsedTerm(searchTerm)

    const term = section || category || parsedTag || parsedSearchedTerm

    return term
  }

  /**
   * @param {Object} deps
   * @param {String} deps.term
   * @param {String} deps.category
   * @return {Array<ArticlePreviewEntity>}
   */
  @cache({ttl: '10 minutes'})
  getNews({category = '', count = 20, makeId, modelId, page = 1, searchTerm = '', section, tag = '', year} = {}) {
    this._log('Getting news items')
    const term = this._getSearchTerm({tag, category, searchTerm, section})
    const offset = (page - 1) * count

    const queryString = this._toQueryString(
      {
        makeId,
        modelId,
        year,
        ...(term && {term}),
        count,
        offset
      },
      {encode: false}
    )

    const API_DOMAIN_URL = this._config.get('CONTENTFUL_API_URL')

    return this._fetcher
      .get(`${API_DOMAIN_URL}/news/?${queryString}`)
      .then(({status, data}) => status === 200 && data)
      .then(data =>
        data
          ? this._newsValueObjectFactory({
              pageItems: count,
              page,
              count: data.count,
              items: data.items.map(this._mapRawArticleToEntity(this._generateTagLink))
            })
          : Promise.reject(new Error('HTTPNewsRepository.getNews() : No news found.'))
      )
  }

  @inlineError
  async subscribeToNewsletter({requestVO}) {
    const API_DOMAIN_URL = this._config.get('API_URL')

    const requestBody = requestVO.toJSON()

    const {userId} = requestBody

    const isLogged = Boolean(userId)

    const url = `${API_DOMAIN_URL}/newsletter/subscribe`
    const authUrl = `${API_DOMAIN_URL}/newsletter/subscribeAuth`

    const requestUrl = isLogged ? authUrl : url

    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._getSubscribeToNewsletterError({message})
        }
      })
      .post(requestUrl, requestBody)

    if (error) return Promise.reject(error)

    return true
  }

  /**
   * @param {Object} deps
   * @param {String} deps.term
   * @return {Array<literal: string, model: number>}
   */
  getByIds({ids}) {
    const API_DOMAIN_URL = this._config.get('CONTENTFUL_API_URL')

    return this._fetcher
      .get(`${API_DOMAIN_URL}/news?ids=${ids.join(',')}`)
      .then(({data: {items}}) => items.map(this._mapRawArticleToEntity(this._generateTagLink)))
  }

  @cache({ttl: '10 minutes'})
  getNewsSuggestions({term}) {
    this._log('Getting news suggestions by %s', term)
    const endpoint = this._config.get('CONTENTFUL_API_URL')

    return this._fetcher.get(`${endpoint}/suggestions/news/?term=${term}`).then(response =>
      response.data.map(data =>
        this._newsSuggestionsValueObjectFactory({
          ...data,
          config: this._config
        })
      )
    )
  }

  /**
   * @param {Object} deps
   * @param {String} deps.term
   * @return {Array<literal: string, model: number>}
   */
  @inlineError
  async getVehicleNews({count = 2, labels = 'pruebas', make, model, offset = 0, page = 1, year}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const queryParams = this._toQueryString({
      count,
      offset,
      make,
      model,
      year,
      labels
    })
    const API_DOMAIN_URL = this._config.get('CONTENTFUL_API_URL')
    const url = `${API_DOMAIN_URL}/news/vehicle?${queryParams}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._getVehicleNewsError({message})
        }
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._newsValueObjectFactory({
      pageItems: count,
      page,
      count: data.count,
      items: data.items.map(this._mapRawArticleToEntity(this._generateTagLink))
    })
  }

  @cache({ttl: '10 minutes'})
  @inlineError
  async getDetailInfo(newsDetailRequestValueObject) {
    const {articleId, locale, slug} = newsDetailRequestValueObject.toJSON()
    const queryString = this._toQueryString(
      {
        slug
      },
      {encode: false}
    )

    const BASE_API_URL = this._config.get('CONTENTFUL_API_URL')
    const PUBLISHED_ARTICLE_API_URL = `${BASE_API_URL}/news/detail/?${queryString}`
    const DRAFT_ARTICLE_API_URL = `${BASE_API_URL}/news/preview/detail/${articleId}`

    const API_URL = articleId ? DRAFT_ARTICLE_API_URL : PUBLISHED_ARTICLE_API_URL

    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [requestError, requestResponse] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._getNewsDetailError({message})
        }
      })
      .get(API_URL)

    if (requestError) return Promise.reject(requestError)

    return this._fromNewsDetailResponseToEntityMapper.setParams({locale}).map({
      newsDetailResponse: requestResponse.data
    })
  }

  @AsyncInlineError()
  async getLatestPlates() {
    const url = `${this._config.get('CONTENTFUL_API_URL')}/plates/latest/`
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return new Error(`[HTTPNewsRepository#getLatestPlates] ${message}`)
        }
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response
    return this._latestPlatesValueObjectFactory(data)
  }
}
